import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TuneIcon from "@mui/icons-material/Tune";
import { useSnackbar } from "notistack";
import insightsApi from "../../api/insightsApi";

// How often to ask the backend whether the proposal is built. The job is tens
// of seconds (embeddings, clustering, one LLM call per topic in count mode), so
// a 3s poll is responsive without hammering the API.
const POLL_INTERVAL_MS = 3000;
// Give up polling after this long. The job writes its own failures back, but a
// job that is killed outright never does — without a deadline the dialog would
// spin forever. The backend retires the abandoned proposal on the next attempt.
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
// Mirrors the backend's own guard rails (insights.services.topics.recompute).
const MIN_TOPICS = 2;
const MAX_TOPICS = 30;
const MAX_TITLE_LENGTH = 200;

// Recompute one open-text question's topics, in two steps.
//
//   edit     — the user edits the topic LIST (rename / remove / add), or
//              switches to "just give me N topics" and lets the LLM name them.
//   building — a job builds the proposal; the report is still untouched.
//   review   — the proposed topics, with the response counts they would get.
//              Only "Apply" writes anything.
//
// The two steps exist because building a proposal costs minutes of embeddings
// and LLM calls whose output is a judgement call, and a rejected proposal has
// to leave the report exactly as it was.
const TopicRecomputeDialog = ({ open, onClose, reportId, question, currentTopics, onApplied }) => {
    const intl = useIntl();
    const { enqueueSnackbar } = useSnackbar();

    const [step, setStep] = useState("edit");
    const [useCount, setUseCount] = useState(false); // false = titles, true = count
    const [titles, setTitles] = useState([]);
    const [count, setCount] = useState(String(MIN_TOPICS));
    const [recompute, setRecompute] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    // Poll handle + a mounted flag, so closing the dialog mid-build stops the
    // polling instead of setting state on an unmounted component.
    const pollRef = useRef(null);
    const liveRef = useRef(true);
    const deadlineRef = useRef(0);

    const stopPolling = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    // Seed the editor from the question's current topics each time it opens, so
    // the starting point is always what the table is showing right now.
    useEffect(() => {
        if (!open) return;
        liveRef.current = true;
        const existing = (currentTopics || []).map((t) => (t && t.title) || "");
        setTitles(existing.length ? existing : ["", ""]);
        setCount(String(Math.max(MIN_TOPICS, existing.length || MIN_TOPICS)));
        setUseCount(false);
        setStep("edit");
        setRecompute(null);
        setError(null);
        setBusy(false);
        return () => {
            liveRef.current = false;
            stopPolling();
        };
    }, [open, currentTopics, stopPolling]);

    const cleanTitles = titles.map((t) => (t || "").trim()).filter(Boolean);
    const duplicateTitles =
        new Set(cleanTitles.map((t) => t.toLowerCase())).size !== cleanTitles.length;
    const countValue = parseInt(count, 10);
    const countValid = Number.isInteger(countValue) && countValue >= MIN_TOPICS && countValue <= MAX_TOPICS;
    const titlesValid =
        cleanTitles.length >= MIN_TOPICS && cleanTitles.length <= MAX_TOPICS && !duplicateTitles;
    const canSubmit = useCount ? countValid : titlesValid;

    const updateTitle = (index, value) => {
        setTitles((prev) => prev.map((t, i) => (i === index ? value : t)));
    };
    const removeTitle = (index) => {
        setTitles((prev) => prev.filter((_, i) => i !== index));
    };
    const addTitle = () => {
        setTitles((prev) => [...prev, ""]);
    };

    // --- build ---------------------------------------------------------------

    const poll = useCallback(
        async (recomputeId) => {
            try {
                const row = await insightsApi.getTopicRecompute(reportId, recomputeId);
                if (!liveRef.current) return;
                setRecompute(row);
                if (row.status === "ready") {
                    stopPolling();
                    setStep("review");
                } else if (row.status === "failed" || row.status === "discarded") {
                    stopPolling();
                    setError(
                        row.error ||
                            intl.formatMessage({
                                id: "topicRecompute.build_failed",
                                defaultMessage: "The topics could not be recomputed.",
                            })
                    );
                    setStep("edit");
                } else if (Date.now() > deadlineRef.current) {
                    stopPolling();
                    setError(
                        intl.formatMessage({
                            id: "topicRecompute.timed_out",
                            defaultMessage:
                                "The topics are taking longer than expected. Try again in a moment.",
                        })
                    );
                    setStep("edit");
                }
            } catch (e) {
                if (!liveRef.current) return;
                stopPolling();
                setError(
                    intl.formatMessage({
                        id: "topicRecompute.poll_failed",
                        defaultMessage: "Lost contact with the topic recompute. Please try again.",
                    })
                );
                setStep("edit");
            }
        },
        [reportId, intl, stopPolling]
    );

    const startBuild = async () => {
        setError(null);
        setBusy(true);
        try {
            const body = useCount
                ? { question, num_topics: countValue }
                : { question, titles: cleanTitles };
            const row = await insightsApi.createTopicRecompute(reportId, body);
            if (!liveRef.current) return;
            setRecompute(row);
            setStep("building");
            stopPolling();
            deadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
            pollRef.current = setInterval(() => poll(row.id), POLL_INTERVAL_MS);
        } catch (e) {
            const detail = e && e.response && e.response.data && e.response.data.error;
            setError(
                detail ||
                    intl.formatMessage({
                        id: "topicRecompute.start_failed",
                        defaultMessage: "Could not start the topic recompute.",
                    })
            );
        } finally {
            if (liveRef.current) setBusy(false);
        }
    };

    // --- confirm / discard ---------------------------------------------------

    const applyProposal = async () => {
        setBusy(true);
        setError(null);
        try {
            const applied = await insightsApi.confirmTopicRecompute(reportId, recompute.id);
            enqueueSnackbar(
                intl.formatMessage({
                    id: "topicRecompute.applied",
                    defaultMessage: "Topics updated",
                }),
                { variant: "success" }
            );
            if (applied.vectors_synced === false) {
                // The report is right, the search index is not — say so rather
                // than let Ask Prompta quietly answer from stale topics.
                enqueueSnackbar(
                    intl.formatMessage({
                        id: "topicRecompute.vectors_not_synced",
                        defaultMessage:
                            "Topics saved, but the search index could not be updated. Ask Prompta may cite the previous topics.",
                    }),
                    { variant: "warning", autoHideDuration: 6000 }
                );
            }
            if (onApplied) onApplied(applied.topics);
            onClose();
        } catch (e) {
            const detail = e && e.response && e.response.data && e.response.data.error;
            setError(
                detail ||
                    intl.formatMessage({
                        id: "topicRecompute.apply_failed",
                        defaultMessage: "Could not apply the new topics.",
                    })
            );
        } finally {
            if (liveRef.current) setBusy(false);
        }
    };

    const discardProposal = async () => {
        stopPolling();
        if (recompute && recompute.status !== "applied") {
            try {
                await insightsApi.discardTopicRecompute(reportId, recompute.id);
            } catch (e) {
                // Nothing was written, so a failed discard is only housekeeping.
                console.error("Error discarding topic proposal:", e);
            }
        }
        onClose();
    };

    // --- steps ---------------------------------------------------------------

    const renderEdit = () => (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <FormattedMessage
                    id="topicRecompute.intro"
                    defaultMessage="Edit the topics for this question, or ask for a number of topics and let Prompta name them. Nothing changes until you review and apply the result."
                />
            </Typography>

            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                <Button
                    size="small"
                    variant={useCount ? "outlined" : "contained"}
                    onClick={() => setUseCount(false)}
                    sx={{ textTransform: "none" }}
                >
                    <FormattedMessage id="topicRecompute.mode_titles" defaultMessage="Use my topic list" />
                </Button>
                <Button
                    size="small"
                    variant={useCount ? "contained" : "outlined"}
                    onClick={() => setUseCount(true)}
                    sx={{ textTransform: "none" }}
                >
                    <FormattedMessage
                        id="topicRecompute.mode_count"
                        defaultMessage="Just pick how many"
                    />
                </Button>
            </Box>

            {useCount ? (
                <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <FormattedMessage
                            id="topicRecompute.count_help"
                            defaultMessage="Responses are grouped into exactly this many topics, and Prompta names each one."
                        />
                    </Typography>
                    <TextField
                        type="number"
                        size="small"
                        value={count}
                        onChange={(e) => setCount(e.target.value)}
                        inputProps={{ min: MIN_TOPICS, max: MAX_TOPICS }}
                        label={intl.formatMessage({
                            id: "topicRecompute.count_label",
                            defaultMessage: "Number of topics",
                        })}
                        error={count !== "" && !countValid}
                        helperText={
                            count !== "" && !countValid
                                ? intl.formatMessage(
                                      {
                                          id: "topicRecompute.count_range",
                                          defaultMessage: "Choose between {min} and {max} topics.",
                                      },
                                      { min: MIN_TOPICS, max: MAX_TOPICS }
                                  )
                                : " "
                        }
                        sx={{ width: 200 }}
                    />
                </Box>
            ) : (
                <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <FormattedMessage
                            id="topicRecompute.titles_help"
                            defaultMessage="Every response is sorted into the closest of these topics. Rename, remove or add as many as you need."
                        />
                    </Typography>
                    {titles.map((title, index) => (
                        <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                value={title}
                                onChange={(e) => updateTitle(index, e.target.value)}
                                inputProps={{ maxLength: MAX_TITLE_LENGTH }}
                                placeholder={intl.formatMessage({
                                    id: "topicRecompute.title_placeholder",
                                    defaultMessage: "Topic name",
                                })}
                            />
                            <Tooltip
                                title={intl.formatMessage({
                                    id: "topicRecompute.remove_topic",
                                    defaultMessage: "Remove topic",
                                })}
                            >
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => removeTitle(index)}
                                        disabled={titles.length <= 1}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    ))}
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={addTitle}
                        disabled={titles.length >= MAX_TOPICS}
                        sx={{ textTransform: "none", mt: 0.5 }}
                    >
                        <FormattedMessage id="topicRecompute.add_topic" defaultMessage="Add topic" />
                    </Button>
                    {duplicateTitles && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            <FormattedMessage
                                id="topicRecompute.duplicate_titles"
                                defaultMessage="Two topics have the same name — give each one a distinct name."
                            />
                        </Alert>
                    )}
                    {!duplicateTitles && cleanTitles.length < MIN_TOPICS && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <FormattedMessage
                                id="topicRecompute.min_titles"
                                defaultMessage="Give at least two topics."
                            />
                        </Alert>
                    )}
                </Box>
            )}
        </Box>
    );

    const renderBuilding = () => (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 5, gap: 2 }}>
            <CircularProgress />
            <Typography variant="body1">
                <FormattedMessage
                    id="topicRecompute.building"
                    defaultMessage="Working out the new topics…"
                />
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                <FormattedMessage
                    id="topicRecompute.building_help"
                    defaultMessage="This takes a minute or two. Your report has not changed yet — you will see the result before anything is applied."
                />
            </Typography>
        </Box>
    );

    const renderReview = () => {
        const topics = (recompute && recompute.preview && recompute.preview.topics) || [];
        const empty = topics.filter((t) => !t.count).length;
        return (
            <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    <FormattedMessage
                        id="topicRecompute.review_intro"
                        defaultMessage="Here is how the responses would be grouped. Applying replaces this question's topics in the report and updates Ask Prompta."
                    />
                </Typography>

                {empty > 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <FormattedMessage
                            id="topicRecompute.empty_topics"
                            defaultMessage="{count, plural, one {# topic matched no responses} other {# topics matched no responses}}. They are kept as empty rows — remove them if you do not want them."
                            values={{ count: empty }}
                        />
                    </Alert>
                )}

                {topics.map((topic, index) => (
                    <Box key={topic.topic_id != null ? topic.topic_id : index} sx={{ mb: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {topic.title}
                            </Typography>
                            <Chip
                                size="small"
                                label={intl.formatMessage(
                                    {
                                        id: "topicsSection.responses_count",
                                        defaultMessage: "{count} responses",
                                    },
                                    { count: topic.count }
                                )}
                                color={topic.count ? "primary" : "default"}
                                variant={topic.count ? "filled" : "outlined"}
                            />
                        </Box>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                            {Object.keys(topic.keywords || {}).slice(0, 6).map((kw) => (
                                <Chip key={kw} size="small" variant="outlined" label={kw} />
                            ))}
                        </Box>
                        {(topic.responses || []).slice(0, 2).map((response, i) => (
                            <Typography
                                key={i}
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5, fontStyle: "italic" }}
                            >
                                “{response}”
                            </Typography>
                        ))}
                        {index < topics.length - 1 && <Divider sx={{ mt: 2 }} />}
                    </Box>
                ))}
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth disableScrollLock>
            <DialogTitle>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TuneIcon color="primary" />
                    <Box>
                        <Typography variant="h6">
                            <FormattedMessage
                                id="topicRecompute.title"
                                defaultMessage="Recompute topics"
                            />
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {question}
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {step === "edit" && renderEdit()}
                {step === "building" && renderBuilding()}
                {step === "review" && renderReview()}
            </DialogContent>

            <DialogActions>
                {step === "edit" && (
                    <>
                        <Button onClick={onClose} disabled={busy}>
                            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                        </Button>
                        <Button
                            variant="contained"
                            onClick={startBuild}
                            disabled={busy || !canSubmit}
                            startIcon={busy ? <CircularProgress size={16} /> : null}
                        >
                            <FormattedMessage
                                id="topicRecompute.preview"
                                defaultMessage="Preview topics"
                            />
                        </Button>
                    </>
                )}
                {step === "building" && (
                    <Button onClick={discardProposal}>
                        <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                    </Button>
                )}
                {step === "review" && (
                    <>
                        <Button onClick={discardProposal} disabled={busy}>
                            <FormattedMessage id="topicRecompute.discard" defaultMessage="Discard" />
                        </Button>
                        <Button onClick={() => setStep("edit")} disabled={busy}>
                            <FormattedMessage id="topicRecompute.back" defaultMessage="Edit again" />
                        </Button>
                        <Button
                            variant="contained"
                            onClick={applyProposal}
                            disabled={busy}
                            startIcon={busy ? <CircularProgress size={16} /> : null}
                        >
                            <FormattedMessage
                                id="topicRecompute.apply"
                                defaultMessage="Apply to report"
                            />
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default TopicRecomputeDialog;
